/**
 * The filing call's answer layout. Every model gets the compact JSON answer
 * (`compactCategorySchema`) - except the categorizer trained for it, which
 * answers in numbered lines with each category's first word and nothing
 * else:
 *
 *   R:Livsmedel
 *   1:Mejeri
 *   2:Dryck
 *
 * About 40% fewer output tokens than compact JSON for the same filing, which
 * is most of a call's time on a CPU (results-2026-10-14 in the distilled line
 * categorizer idea). A model never trained on it files worse in it, and a
 * hosted model cannot be held to it, so the layout follows the model:
 * `filingLayoutFor`. It is enforced with a GBNF grammar, which llama.cpp's
 * server takes in place of a schema; nothing else sends it.
 *
 * The prompt and grammar here are the ones the categorizer was trained on
 * (`apps/ios/tools/bench/distill-v4.ts --format mixed`): change them and the
 * trained model has to be trained again.
 */

import type { CategoryOption } from './categories.js';

export type FilingLayout = 'json' | 'lines';

/** How a provider picks the layout: by the model (`auto`), or one for every model. */
export type FilingLayoutSetting = 'auto' | FilingLayout;

/** The trained categorizer is served under a name with this in it (llama-server `--alias kvitto-categorizer-12b`). */
const TRAINED_CATEGORIZER = /kvitto/i;

/** The layout a filing call to `model` should ask for. */
export function filingLayoutFor(model: string | null | undefined, setting: FilingLayoutSetting = 'auto'): FilingLayout {
  if (setting !== 'auto') return setting;
  return model && TRAINED_CATEGORIZER.test(model) ? 'lines' : 'json';
}

/**
 * Each offered category's short name: the first word of its name ("Skafferi &
 * fryst" -> "Skafferi"). A first word two categories share stays whole for both.
 */
export function shortCategoryNames(categories: readonly CategoryOption[]): Map<string, string> {
  const first = (key: string) => key.split(' ')[0]!.replace(/,$/, '');
  const counts = new Map<string, number>();
  for (const category of categories) counts.set(first(category.key), (counts.get(first(category.key)) ?? 0) + 1);
  return new Map(categories.map((category) => [category.key, counts.get(first(category.key)) === 1 ? first(category.key) : category.key]));
}

const JSON_INSTRUCTION = '\n\nAnswer with JSON only';

/** What the lines layout asks for, in place of the compact prompt's JSON instruction. */
const LINES_INSTRUCTION =
  'Answer with plain lines and nothing else: first "R:" and the category for the whole receipt, then one line for every numbered line, ' +
  'in order, as "<number>:<category>" - each category written as the first word of a category name only ' +
  '(for "Skafferi, bröd & fryst" write "Skafferi").';

/**
 * The lines layout's system prompt, from the compact one
 * (`buildCompactCategorySystemPrompt`): its JSON instruction replaced, and the
 * rule to copy a name whole dropped.
 */
export function linesCategorySystemPrompt(compactPrompt: string): string {
  const body = compactPrompt.slice(0, compactPrompt.lastIndexOf(JSON_INSTRUCTION));
  return `${body.replace(', and write the key exactly as it appears', '')}\n\n${LINES_INSTRUCTION}`;
}

/**
 * The GBNF grammar for a lines answer: `R:` and a category, then each line's
 * number and a category, in order, and nothing after the last one (Gemma's
 * chat template trims the answer it is trained on).
 */
export function linesCategoryGrammar(shortNames: readonly string[], lineNumbers: readonly number[]): string {
  const literal = (text: string) => JSON.stringify(text);
  const rows = [`${literal('R:')} cat`, ...lineNumbers.map((line) => `${literal(`${line}:`)} cat`)];
  return `root ::= ${rows.join(` ${literal('\n')} `)}\ncat ::= ${[...new Set(shortNames)].map(literal).join(' | ')}`;
}

/** A lines answer as `{ receipt, lines }`, short names as written. Rows that are neither are skipped. */
export function parseLinesAnswer(text: string): { receipt: string | null; lines: Map<number, string> } {
  const lines = new Map<number, string>();
  let receipt: string | null = null;
  for (const row of text.split('\n')) {
    const match = /^\s*(R|\d{1,3})\s*:\s*(.+?)\s*$/.exec(row);
    if (!match) continue;
    if (match[1] === 'R') receipt = match[2]!;
    else lines.set(Number(match[1]), match[2]!);
  }
  return { receipt, lines };
}

/**
 * A lines answer in the full filing answer's shape (as `filingFromCompact`
 * gives a compact one), each short name back to the category key it stands
 * for; a name that is none of them is kept as written, for the resolver to
 * drop.
 */
export function filingFromLines(text: string, shortNames: ReadonlyMap<string, string>): Record<string, unknown> {
  const keyOf = new Map([...shortNames].map(([key, short]) => [short, key]));
  const parsed = parseLinesAnswer(text);
  const key = (name: string | null) => (name === null ? null : (keyOf.get(name) ?? name));
  return {
    receiptCategory: key(parsed.receipt),
    otherCategories: [],
    items: [...parsed.lines].map(([line, name]) => ({ line, category: key(name) })),
  };
}
